import TasksAPI from '../../api/tasks';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const taskTypes = {
  SET_TASKS: 'SET_TASKS',
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  REMOVE_TASK: 'REMOVE_TASK',
  SET_TASKS_UI_FLAG: 'SET_TASKS_UI_FLAG',
  SET_TASKS_META: 'SET_TASKS_META',
  SET_TASKS_COUNTS: 'SET_TASKS_COUNTS',
};

export const state = {
  records: [],
  meta: {
    count: 0,
    current_page: 1,
    total_pages: 0,
  },
  counts: {
    overdue: 0,
    due_today: 0,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getTasks: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getCounts: $state => $state.counts,
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit(taskTypes.SET_TASKS_UI_FLAG, { isFetching: true });
    try {
      const response = await TasksAPI.get(params);
      commit(taskTypes.SET_TASKS, response.data);
      const { headers } = response;
      commit(taskTypes.SET_TASKS_META, {
        count: parseInt(headers['x-total-count'] || response.data.length, 10),
        current_page: parseInt(headers['x-page'] || 1, 10),
        total_pages: parseInt(headers['x-total-pages'] || 1, 10),
      });
    } catch (error) {
      // Tratar erro
    } finally {
      commit(taskTypes.SET_TASKS_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, taskData) => {
    commit(taskTypes.SET_TASKS_UI_FLAG, { isCreating: true });
    try {
      const response = await TasksAPI.create(taskData);
      commit(taskTypes.ADD_TASK, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(taskTypes.SET_TASKS_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, taskData) => {
    const { id, ...updateData } = taskData;
    commit(taskTypes.SET_TASKS_UI_FLAG, { isUpdating: true });
    try {
      const response = await TasksAPI.update(id, updateData);
      commit(taskTypes.UPDATE_TASK, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(taskTypes.SET_TASKS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, taskId) => {
    commit(taskTypes.SET_TASKS_UI_FLAG, { isDeleting: true });
    try {
      await TasksAPI.delete(taskId);
      commit(taskTypes.REMOVE_TASK, taskId);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(taskTypes.SET_TASKS_UI_FLAG, { isDeleting: false });
    }
  },

  // Alterna o status de conclusao de uma tarefa (toggle).
  // Reusado pelo popup de notificacoes e pelo board de MyTasks.
  toggleComplete: async ({ commit }, task) => {
    const response = await TasksAPI.update(task.id, {
      completed: !task.completed,
    });
    commit(taskTypes.UPDATE_TASK, response.data);
    return response.data;
  },

  // Action para recarregar tarefas de um contato específico
  refreshContactTasks: async ({ commit, dispatch }, contactId) => {
    try {
      await dispatch('get', {
        relatable_type: 'Contact',
        relatable_id: contactId,
        page: 1,
        limit: 20,
      });

      // Notificar kanban se necessário
      dispatch(
        'kanban/refreshCardData',
        {
          cardId: `contact_${contactId}`,
          type: 'contact',
        },
        { root: true }
      );
    } catch (error) {
      console.error('Erro ao recarregar tarefas do contato:', error);
    }
  },

  // Action para recarregar tarefas de uma conversa específica
  refreshConversationTasks: async ({ commit, dispatch }, conversationId) => {
    try {
      await dispatch('get', {
        relatable_type: 'Conversation',
        relatable_id: conversationId,
        page: 1,
        limit: 20,
      });

      // Notificar kanban se necessário
      dispatch(
        'kanban/refreshCardData',
        {
          cardId: conversationId,
          type: 'conversation',
        },
        { root: true }
      );
    } catch (error) {
      console.error('Erro ao recarregar tarefas da conversa:', error);
    }
  },

  // Busca contagens de tarefas (overdue + due_today) do usuário atual
  // para os badges da sidebar. Loga o erro mas não propaga para não quebrar a sidebar.
  fetchCounts: async ({ commit }) => {
    try {
      const response = await TasksAPI.counts();
      commit(taskTypes.SET_TASKS_COUNTS, response.data);
    } catch (error) {
      // Não propaga — sidebar não pode quebrar se counts falhar.
      // Erros ERR_CANCELED (abort) são esperados em navegação rápida.
      if (error?.code !== 'ERR_CANCELED') {
        // eslint-disable-next-line no-console
        console.error('[tasks/fetchCounts] failed to load task counts', error);
      }
    }
  },
};

export const mutations = {
  [taskTypes.SET_TASKS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [taskTypes.SET_TASKS_META]($state, data) {
    $state.meta = data;
  },

  [taskTypes.SET_TASKS_COUNTS]($state, data) {
    $state.counts = {
      overdue: data.overdue || 0,
      due_today: data.due_today || 0,
    };
  },

  [taskTypes.SET_TASKS]: MutationHelpers.set,
  [taskTypes.ADD_TASK]: MutationHelpers.create,
  [taskTypes.UPDATE_TASK]: MutationHelpers.update,
  [taskTypes.REMOVE_TASK]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
